// COPY of web/src/TrendsModal.tsx — keep in sync.

// Longitudinal trends for a single vehicle. Plots key engine-health
// metrics over time (across all analysed recordings), runs a linear
// least-squares fit per metric, and surfaces a fail-mode warning when
// the projection crosses a known threshold within ~30 days.
//
// Duplicated in admin/src/TrendsModal.tsx — keep in sync.

import { useEffect, useMemo, useState } from 'react';
import { TrendSample, TrendsResponse, VehicleMetricBaseline } from './api';
import { RepairEvent, REPAIR_KIND_LABELS } from './api';

interface Props {
  vehicleId: number;
  vehicleName?: string;
  fetchUrl?: string;     // override for admin (defaults to /api/vehicles/...)
  /** Override for the repair-events fetch URL when admin uses
   *  /api/admin/vehicles/:id/repair-events. */
  repairEventsUrl?: string;
  onClose: () => void;
}

export function TrendsModal({
  vehicleId, vehicleName, fetchUrl, repairEventsUrl, onClose,
}: Props) {
  const [samples, setSamples] = useState<TrendSample[] | null>(null);
  const [metricStats, setMetricStats] = useState<VehicleMetricBaseline[]>([]);
  const [repairEvents, setRepairEvents] = useState<RepairEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const url = fetchUrl ?? `/api/vehicles/${vehicleId}/trends`;
    const evUrl = repairEventsUrl ?? `/api/vehicles/${vehicleId}/repair-events`;
    Promise.all([
      fetch(url, { credentials: 'same-origin' }).then((r) => r.json()),
      // Repair events are best-effort — if the endpoint is missing
      // (older deploy) we still want the trends to render.
      fetch(evUrl, { credentials: 'same-origin' })
        .then((r) => r.ok ? r.json() : { events: [] })
        .catch(() => ({ events: [] })),
    ])
      .then(([trends, events]) => {
        if (cancelled) return;
        if ('error' in trends && trends.error) { setError(trends.error); return; }
        const t = trends as TrendsResponse;
        setSamples(t.samples);
        setMetricStats(t.metric_stats ?? []);
        const ev = (events as { events?: RepairEvent[] })?.events ?? [];
        setRepairEvents(ev);
      })
      .catch((e) => { if (!cancelled) setError((e as Error).message); });
    return () => { cancelled = true; };
  }, [vehicleId, fetchUrl, repairEventsUrl]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 1100, width: '95%', maxHeight: '92vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>
            Trends — history over time
            {vehicleName && (
              <span className="muted" style={{ fontWeight: 400, fontSize: '0.85rem' }}>
                {' '}— {vehicleName}
              </span>
            )}
          </h3>
          <button className="modal-close" onClick={onClose} aria-label="close">×</button>
        </div>

        {error && <div className="error">{error}</div>}
        {!samples ? (
          <div className="muted">loading…</div>
        ) : samples.length === 0 ? (
          <div className="muted">No analysed recordings for this vehicle yet.</div>
        ) : (
          <TrendsBody samples={samples} repairEvents={repairEvents} metricStats={metricStats} />
        )}
      </div>
    </div>
  );
}

// ---- Body: pick metrics, render charts --------------------------------

// Threshold above which we trust Layer-1 alignment enough to plot
// per-cylinder series in template-frame (persistent-ID) order. Below
// this, the rotation is ambiguous enough that we'd risk visually
// shuffling data, which is worse than just labelling by slot.
const ALIGNMENT_CONFIDENCE_THRESHOLD = 0.5;
// Fraction of recordings that must clear the threshold before the
// whole plot switches to template-frame ordering. Mixing the two
// would defeat the purpose (the per-cyl line would jump anyway).
const ALIGNMENT_QUORUM = 0.5;

type CylAxis = 'cyl' | 'slot';

function chooseCylinderAxis(samples: TrendSample[]): CylAxis {
  const eligible = samples.filter((s) => s.num_cylinders != null);
  if (eligible.length === 0) return 'slot';
  const aligned = eligible.filter(
    (s) => s.cylinder_alignment != null
        && s.cylinder_alignment.confidence >= ALIGNMENT_CONFIDENCE_THRESHOLD
        && s.cylinder_energies_by_cyl != null,
  );
  return aligned.length / eligible.length >= ALIGNMENT_QUORUM ? 'cyl' : 'slot';
}

function cylLabel(axis: CylAxis, k: number): string {
  // Persistent-ID labels (P-A, P-B, ...) deliberately don't reuse
  // "cyl 1, cyl 2" — until Step E lets the user assign clusters to
  // physical cylinders, the persistent ID is just an opaque tag.
  return axis === 'cyl' ? `P-${String.fromCharCode(65 + k)}` : `slot ${k + 1}`;
}

function TrendsBody({
  samples, repairEvents, metricStats,
}: {
  samples: TrendSample[];
  repairEvents: RepairEvent[];
  metricStats: VehicleMetricBaseline[];
}) {
  // Index by metric_key for quick chart-level lookups.
  const baselinesByKey = useMemo(() => {
    const m = new Map<string, VehicleMetricBaseline>();
    for (const s of metricStats) m.set(s.metric_key, s);
    return m;
  }, [metricStats]);
  // Sample count summary
  const numCyl = samples.find((s) => s.num_cylinders != null)?.num_cylinders ?? 0;
  const dateFrom = new Date(samples[0].recorded_at).toLocaleDateString();
  const dateTo   = new Date(samples[samples.length - 1].recorded_at).toLocaleDateString();
  const axis = chooseCylinderAxis(samples);
  // For the explanatory banner: how many samples were available, how
  // many had high-confidence alignment, and what the median template
  // count looks like.
  const alignedCount = samples.filter(
    (s) => s.cylinder_alignment != null
        && s.cylinder_alignment.confidence >= ALIGNMENT_CONFIDENCE_THRESHOLD,
  ).length;
  const latestTemplateCount = samples
    .map((s) => s.cylinder_alignment?.template_count ?? 0)
    .reduce((m, v) => Math.max(m, v), 0);
  return (
    <>
      <div className="muted" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
        {samples.length} recordings · {dateFrom} → {dateTo}
        {numCyl > 0 && ` · ${numCyl}-cylinder engine`}
        {' · '}5-min binning
      </div>
      <div style={{
        background: 'var(--panel-2)',
        border: '1px solid var(--border)',
        borderLeft: '3px solid #10b981',
        borderRadius: 4,
        padding: '0.4rem 0.7rem',
        fontSize: '0.78rem',
        marginBottom: '0.5rem',
        lineHeight: 1.4,
      }}>
        <strong>Confidence indicators</strong>: each chart point is
        opacity-graded by its per-recording confidence (faded = low
        confidence = trust the value less). The thin green line at
        the bottom of each chart is the confidence trace over time on
        a 0–1 scale. Top-level metrics use cycle-fingerprint alignment
        confidence; per-cyl charts use per-cyl health confidence (cycle
        count + cycle-to-cycle variance). Once the per-cyl SVM
        classifier ships, these will be replaced with the classifier's
        per-class probability.
        <br /><br />
        <strong>Baseline band (Step J)</strong>: when present, the
        faint green horizontal band on each chart is the per-vehicle
        Welford baseline (mean ± 2σ across all this vehicle's
        recordings). Replaces the global hand-tuned thresholds. Drift
        events fire when the recent window strays {'>'}3σ from this
        baseline (Step L); see admin → vehicle → drift events.
        Hover the band for n + mean + 2σ.
      </div>
      {numCyl > 0 && (
        <div style={{
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '0.4rem 0.6rem',
          fontSize: '0.78rem',
          marginBottom: '0.5rem',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
          alignItems: 'baseline',
        }}>
          <span><strong>Per-cyl axis:</strong> {axis === 'cyl' ? 'persistent ID (P-A, P-B…)' : 'recording slot (slot 1, slot 2…)'}</span>
          <span className="muted">
            {alignedCount}/{samples.length} recordings aligned ≥ {(ALIGNMENT_CONFIDENCE_THRESHOLD * 100).toFixed(0)}%
            {latestTemplateCount > 0 && ` · template folds ${latestTemplateCount} clips`}
          </span>
        </div>
      )}

      <Section title="Engine global">
        <ChartGrid>
          <TrendChart
            samples={samples} repairEvents={repairEvents} title="Consensus RPM" unit="RPM"
            extract={(s) => s.rpm}
            extractGauge={(s) => s.gauge_rpm}
            direction="closer-to-gauge"
          />
          <TrendChart
            samples={samples} repairEvents={repairEvents}
            baseline={baselinesByKey.get('smoothness') ?? null}
            title="Smoothness score" unit=""
            extract={(s) => s.smoothness_score}
            direction="higher-better"
            warnAt={75} criticalAt={50}
          />
          <TrendChart
            samples={samples} repairEvents={repairEvents}
            baseline={baselinesByKey.get('exhaust_leak') ?? null}
            title="Exhaust leak score" unit=""
            extract={(s) => s.exhaust_leak_score}
            direction="lower-better"
            warnAt={0.50} criticalAt={0.65}
          />
          <TrendChart
            samples={samples} repairEvents={repairEvents}
            baseline={baselinesByKey.get('vacuum_leak') ?? null}
            title="Vacuum leak score" unit=""
            extract={(s) => s.vacuum_leak_score}
            direction="lower-better"
            warnAt={0.40} criticalAt={0.65}
          />
        </ChartGrid>
      </Section>

      {numCyl > 0 && (
        <Section title="Per-cylinder">
          <PerCylinderTrend
            samples={samples} repairEvents={repairEvents} numCyl={numCyl} axis={axis}
            title="Energy (% of cohort)"
            extract={(s, k) => (axis === 'cyl' ? s.cylinder_energies_by_cyl : s.cylinder_energies)?.[k] ?? null}
            direction="closer-to-100"
            warnDeviation={15} criticalDeviation={25}
          />
          <PerCylinderTrend
            samples={samples} repairEvents={repairEvents} numCyl={numCyl} axis={axis}
            title="Missed events / clip"
            extract={(s, k) => (axis === 'cyl' ? s.cylinder_missed_by_cyl : s.cylinder_missed)?.[k] ?? null}
            direction="lower-better"
            warnAt={3} criticalAt={5}
          />
          <PerCylinderTrend
            samples={samples} repairEvents={repairEvents} numCyl={numCyl} axis={axis}
            title="Knock severity (× cohort)"
            extract={(s, k) => (axis === 'cyl' ? s.cylinder_knock_by_cyl : s.cylinder_knock)?.[k] ?? null}
            direction="lower-better"
            warnAt={1.5} criticalAt={2.5}
          />
        </Section>
      )}

      <Section title="Accessory drive (SNR over time)">
        {(['alternator', 'ac_compressor', 'power_steering', 'water_pump'] as const).map((name) => (
          <TrendChart
            key={name}
            samples={samples} repairEvents={repairEvents} title={name.replace('_', ' ')} unit="SNR"
            extract={(s) => {
              const a = s.accessories?.find((x) => x.name === name);
              return a?.snr ?? null;
            }}
            direction="higher-better"
            warnAt={6} criticalAt={3}
          />
        ))}
      </Section>

      <Section title="Bearings & events">
        <ChartGrid>
          <TrendChart
            samples={samples} repairEvents={repairEvents} title="Bearing peaks (#)" unit=""
            extract={(s) => s.bearing_peak_count}
            direction="lower-better"
            warnAt={5} criticalAt={10}
          />
          <TrendChart
            samples={samples} repairEvents={repairEvents} title="Max bearing-band SNR" unit=""
            extract={(s) => s.max_bearing_snr}
            direction="lower-better"
            warnAt={20} criticalAt={50}
          />
          <TrendChart
            samples={samples} repairEvents={repairEvents} title="Total transient events" unit=""
            extract={(s) => s.num_events}
            direction="lower-better"
            warnAt={5} criticalAt={15}
          />
          <TrendChart
            samples={samples} repairEvents={repairEvents} title="Broadband bursts (backfires)" unit=""
            extract={(s) => s.num_broadband_events}
            direction="lower-better"
            warnAt={1} criticalAt={3}
          />
        </ChartGrid>
      </Section>

      {repairEvents.length > 0 && (
        <Section title="Repair effects (before vs after)">
          <RepairEffectsPanel samples={samples} repairEvents={repairEvents} />
        </Section>
      )}
    </>
  );
}

// ---- Layout helpers --------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: '1rem' }}>
      <h4 style={{ margin: '0 0 0.5rem 0' }}>{title}</h4>
      {children}
    </div>
  );
}

function ChartGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '0.5rem',
    }}>{children}</div>
  );
}

// ---- Linear least squares + threshold projection ---------------------

type Direction = 'higher-better' | 'lower-better' | 'closer-to-100' | 'closer-to-gauge';

interface LinearFit {
  slope: number;       // value per ms
  intercept: number;
  r2: number;
  /** Two-sided p-value for H0: slope = 0. Computed via the standard
   *  t-statistic on the slope (slope / SE_slope), df = n - 2,
   *  approximated by normal CDF (df is large enough for our trend
   *  point counts). p < 0.05 → trend is statistically distinguishable
   *  from a flat line. */
  slopeP: number;
  /** Number of data points the fit is based on. */
  n: number;
}

function normalCdf(x: number): number {
  // Abramowitz-Stegun, max abs err ~7e-8.
  const a1 =  0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const xa = Math.abs(x) / Math.sqrt(2);
  const t = 1 / (1 + p * xa);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-xa * xa);
  return 0.5 * (1 + sign * y);
}

function linearFit(xs: number[], ys: number[]): LinearFit | null {
  const n = xs.length;
  if (n < 3) return null;
  const xBar = xs.reduce((s, v) => s + v, 0) / n;
  const yBar = ys.reduce((s, v) => s + v, 0) / n;
  let num = 0, denX = 0, denY = 0;
  for (let i = 0; i < n; i++) {
    num  += (xs[i] - xBar) * (ys[i] - yBar);
    denX += (xs[i] - xBar) ** 2;
    denY += (ys[i] - yBar) ** 2;
  }
  if (denX <= 0) return null;
  const slope = num / denX;
  const intercept = yBar - slope * xBar;
  const r2 = denY <= 0 ? 0 : (num * num) / (denX * denY);
  // Standard error of the slope. SSE = SST × (1 - r²); residual
  // variance σ² = SSE / (n - 2); SE(slope) = σ / sqrt(SS_x).
  let slopeP = 1;
  if (n >= 4 && denX > 0 && denY > 0) {
    const sse = denY * (1 - r2);
    const sigma2 = sse / Math.max(1, n - 2);
    const seSlope = Math.sqrt(sigma2 / denX);
    if (seSlope > 0 && Number.isFinite(seSlope)) {
      const tstat = slope / seSlope;
      // Two-sided normal-approx p (df is large enough for our n).
      slopeP = 2 * (1 - normalCdf(Math.abs(tstat)));
      if (!Number.isFinite(slopeP) || slopeP < 0) slopeP = 1;
      if (slopeP > 1) slopeP = 1;
    }
  }
  return { slope, intercept, r2, slopeP, n };
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface TrendChartProps {
  samples: TrendSample[];
  title: string;
  unit?: string;
  extract: (s: TrendSample) => number | null;
  /** Per-recording confidence in [0,1] for THIS metric. Drives point
   *  opacity + the confidence sub-trace. Defaults to alignment
   *  confidence (good proxy for top-level metrics); per-cyl trends
   *  override with the per-cyl confidence. */
  extractConf?: (s: TrendSample) => number | null;
  /** When set, plotted as a secondary line (e.g. gauge RPM next to ICA RPM). */
  extractGauge?: (s: TrendSample) => number | null;
  direction: Direction;
  warnAt?: number;
  criticalAt?: number;
  /** For closer-to-100 metrics: |value - 100| crossing N triggers warning. */
  warnDeviation?: number;
  criticalDeviation?: number;
  /** Repair events to overlay as vertical lines at their occurred_at
   *  timestamps. Color-coded by cylinder; whole-engine events get
   *  muted gray. Tooltip via SVG <title>. */
  repairEvents?: RepairEvent[];
  /** Per-vehicle Welford baseline for THIS metric (Step J). When
   *  present, the chart renders a horizontal reference band at
   *  mean ± 2σ. Acts as the "what's normal for THIS vehicle?" reference
   *  point against which drift is judged. */
  baseline?: VehicleMetricBaseline | null;
}

function TrendChart({
  samples, title, unit, extract, extractGauge, extractConf, direction,
  warnAt, criticalAt, warnDeviation, criticalDeviation,
  repairEvents, baseline,
}: TrendChartProps) {
  // Default confidence source: alignment confidence (per-recording).
  // Per-cyl charts override with per-cyl confidence via extractConf.
  const confSource = extractConf
    ?? ((s: TrendSample) => s.cylinder_alignment?.confidence ?? null);

  const rawPoints: Array<{ t: number; v: number; conf: number | null }> = [];
  const rawGaugePts: Array<{ t: number; v: number; conf: number | null }> = [];
  for (const s of samples) {
    const v = extract(s);
    const cf = confSource(s);
    if (v != null && Number.isFinite(v)) rawPoints.push({ t: s.recorded_at, v, conf: cf });
    if (extractGauge) {
      const g = extractGauge(s);
      if (g != null && Number.isFinite(g)) rawGaugePts.push({ t: s.recorded_at, v: g, conf: cf });
    }
  }
  if (rawPoints.length === 0) {
    return (
      <ChartFrame title={title}>
        <div className="muted" style={{ fontSize: '0.8rem' }}>no data</div>
      </ChartFrame>
    );
  }

  // Bin into 5-min wall-clock buckets. Multiple recordings in the
  // same window become one averaged point — keeps the chart readable
  // on long sessions and gives the slope test a fair n. Per-bin
  // confidence is the mean of contributing recordings' confidence.
  const points = binInto5min(rawPoints);
  const gaugePts = binInto5min(rawGaugePts);

  const fit = linearFit(points.map((p) => p.t), points.map((p) => p.v));
  const lastVal = points[points.length - 1].v;
  const lastT   = points[points.length - 1].t;
  const tMin = points[0].t, tMax = lastT;
  let vMin = Math.min(...points.map((p) => p.v));
  let vMax = Math.max(...points.map((p) => p.v));
  if (gaugePts.length) {
    vMin = Math.min(vMin, ...gaugePts.map((p) => p.v));
    vMax = Math.max(vMax, ...gaugePts.map((p) => p.v));
  }
  if (vMax - vMin < 1e-6) { vMin -= 1; vMax += 1; }
  // Pad y-range
  const yPad = (vMax - vMin) * 0.1;
  vMin -= yPad; vMax += yPad;

  // Failure projection
  const warning = computeWarning({
    fit, lastVal, lastT, direction,
    warnAt, criticalAt, warnDeviation, criticalDeviation,
  });

  // Plot dimensions. H is taller than before to make room for the
  // confidence sub-trace at the bottom (alignment / per-cyl confidence
  // over the same x-axis as the main metric).
  const W = 320, H = 140;
  const pad = { l: 36, r: 6, t: 6, b: 32 };  // bottom hosts conf band + labels
  const plotW = W - pad.l - pad.r;
  const plotH = H - pad.t - pad.b;
  const xToPx = (t: number): number => {
    if (tMax === tMin) return pad.l + plotW / 2;
    return pad.l + ((t - tMin) / (tMax - tMin)) * plotW;
  };
  const yToPx = (v: number): number =>
    pad.t + (1 - (v - vMin) / (vMax - vMin)) * plotH;
  // Confidence band geometry: 12px tall, sits just below the main plot.
  const confBandTop = pad.t + plotH + 2;
  const confBandH = 12;
  const confToY = (c: number): number =>
    confBandTop + (1 - Math.max(0, Math.min(1, c))) * confBandH;
  // Whether any point carries confidence info — drives sub-trace render.
  const hasConf = points.some((p) => p.conf != null);

  // Threshold lines
  const threshLines: Array<{ y: number; color: string; label: string }> = [];
  if (direction === 'higher-better' || direction === 'lower-better') {
    if (warnAt != null) threshLines.push({
      y: warnAt, color: '#f59e0b', label: `warn ${warnAt}`,
    });
    if (criticalAt != null) threshLines.push({
      y: criticalAt, color: 'var(--danger)', label: `critical ${criticalAt}`,
    });
  }

  // Slope-significance footer: distinguishes "real trend" from "flat
   // line of noise" with an explicit p-value rather than just the r²
   // (which can read high from very few points). Only relevant when
   // there's a fit AND enough points for the test to have power.
  const slopeStatsLine = fit && fit.n >= 4
    ? formatSlopeStats(fit, unit)
    : null;

  return (
    <ChartFrame title={title} warning={warning} statsFooter={slopeStatsLine}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block' }}>
        {/* Step J — per-vehicle Welford baseline band (mean ± 2σ).
            Drawn first so threshold lines + data sit on top. The
            band is rendered only when the metric value is in the
            visible y-range, so we don't waste pixels on out-of-range
            baselines. */}
        {baseline && baseline.n >= 3 && (() => {
          const lo = Math.max(vMin, baseline.warn_low);
          const hi = Math.min(vMax, baseline.warn_high);
          if (hi <= lo) return null;
          return (
            <g>
              <title>{`Vehicle baseline (n=${baseline.n}): mean ${baseline.mean.toFixed(2)} ± ${(2 * baseline.std).toFixed(2)} (2σ)`}</title>
              <rect
                x={pad.l} y={yToPx(hi)}
                width={plotW} height={Math.max(1, yToPx(lo) - yToPx(hi))}
                fill="#10b981" opacity="0.06"
              />
              <line
                x1={pad.l} x2={W - pad.r}
                y1={yToPx(baseline.mean)} y2={yToPx(baseline.mean)}
                stroke="#10b981" strokeWidth="0.6" strokeDasharray="4 3" opacity="0.5"
              />
            </g>
          );
        })()}
        {/* Threshold lines */}
        {threshLines.map((tl, i) => {
          if (tl.y < vMin || tl.y > vMax) return null;
          const y = yToPx(tl.y);
          return (
            <g key={i}>
              <line x1={pad.l} y1={y} x2={W - pad.r} y2={y}
                    stroke={tl.color} strokeWidth="0.5" strokeDasharray="3 2" opacity="0.6" />
              <text x={W - pad.r - 2} y={y - 1} fill={tl.color}
                    fontSize="9" textAnchor="end">{tl.label}</text>
            </g>
          );
        })}
        {/* Y-axis labels */}
        <text x={4} y={pad.t + 8} fill="var(--muted)" fontSize="9">
          {fmtY(vMax, unit)}
        </text>
        <text x={4} y={H - pad.b + 2} fill="var(--muted)" fontSize="9">
          {fmtY(vMin, unit)}
        </text>
        {/* X-axis labels (start/end with date+hour). Sit BELOW the
            confidence band so they don't overlap the sub-trace. */}
        <text x={pad.l} y={H - 4} fill="var(--muted)" fontSize="9">
          {fmtX(tMin)}
        </text>
        <text x={W - pad.r} y={H - 4} fill="var(--muted)" fontSize="9" textAnchor="end">
          {fmtX(tMax)}
        </text>
        {/* Repair-event vertical lines (Step H). Color-coded by
            cylinder via CYL_COLORS; whole-engine events (cyl=null)
            in muted gray. Triangle marker at the top so they're
            recognizable even if the user isn't expecting them.
            Hover via SVG <title>. */}
        {repairEvents?.map((ev) => {
          if (ev.occurred_at < tMin - MS_PER_HOUR || ev.occurred_at > tMax + MS_PER_HOUR) return null;
          const x = xToPx(ev.occurred_at);
          const color = ev.cylinder_id != null
            ? CYL_COLORS[(ev.cylinder_id - 1) % CYL_COLORS.length]
            : 'var(--muted)';
          const cylLabel = ev.cylinder_id != null ? `Cyl ${ev.cylinder_id}` : 'Engine-wide';
          const kindLabel = REPAIR_KIND_LABELS[ev.repair_kind] ?? ev.repair_kind;
          const dateLabel = new Date(ev.occurred_at).toLocaleString();
          return (
            <g key={ev.id}>
              <title>{`${cylLabel} · ${kindLabel} · ${dateLabel}${ev.notes ? ` · ${ev.notes}` : ''}`}</title>
              <line
                x1={x} x2={x}
                y1={pad.t} y2={pad.t + plotH}
                stroke={color} strokeWidth="1.0" strokeDasharray="3 2"
                opacity="0.75"
              />
              {/* Small triangle marker at the top */}
              <polygon
                points={`${x - 3},${pad.t} ${x + 3},${pad.t} ${x},${pad.t + 5}`}
                fill={color}
              />
            </g>
          );
        })}
        {/* Linear fit line */}
        {fit && (
          <line
            x1={xToPx(tMin)} y1={yToPx(fit.intercept + fit.slope * tMin)}
            x2={xToPx(tMax)} y2={yToPx(fit.intercept + fit.slope * tMax)}
            stroke="var(--muted)" strokeWidth="0.8" strokeDasharray="2 2"
          />
        )}
        {/* Gauge series (if any) */}
        {gaugePts.length >= 2 && (
          <polyline
            points={gaugePts.map((p) => `${xToPx(p.t)},${yToPx(p.v)}`).join(' ')}
            fill="none" stroke="#f59e0b" strokeWidth="1" strokeDasharray="3 3"
          />
        )}
        {/* Primary series — line, then per-point circles. Each point's
            opacity is graded by its confidence: low-confidence
            recordings stay visible (so you see them dropping) but
            fade vs high-confidence neighbors. */}
        <polyline
          points={points.map((p) => `${xToPx(p.t)},${yToPx(p.v)}`).join(' ')}
          fill="none" stroke="var(--accent)" strokeWidth="1.2" opacity="0.8"
        />
        {points.map((p, i) => (
          <circle key={i} cx={xToPx(p.t)} cy={yToPx(p.v)} r="2"
                  fill="var(--accent)" opacity={confToOpacity(p.conf)} />
        ))}
        {/* Confidence sub-trace. Y range fixed at 0..1; thin band
            below the main plot. Background tinted faintly so the
            band is visually distinct even when conf stays near 1. */}
        {hasConf && (
          <>
            <rect x={pad.l} y={confBandTop} width={plotW} height={confBandH}
                  fill="var(--border)" opacity="0.3" />
            {/* Reference lines at conf = 0.5 and 1.0 */}
            <line x1={pad.l} x2={W - pad.r}
                  y1={confToY(0.5)} y2={confToY(0.5)}
                  stroke="var(--muted)" strokeWidth="0.4" strokeDasharray="2 2" />
            <line x1={pad.l} x2={W - pad.r}
                  y1={confToY(1)} y2={confToY(1)}
                  stroke="var(--muted)" strokeWidth="0.4" />
            <text x={4} y={confToY(1) + 3} fill="var(--muted)" fontSize="7">1</text>
            <text x={4} y={confToY(0) + 3} fill="var(--muted)" fontSize="7">0</text>
            <text x={pad.l + 2} y={confBandTop - 1} fill="var(--muted)" fontSize="7">
              confidence
            </text>
            {/* Conf line + per-point dots */}
            <polyline
              points={points
                .filter((p) => p.conf != null)
                .map((p) => `${xToPx(p.t)},${confToY(p.conf!)}`).join(' ')}
              fill="none" stroke="#10b981" strokeWidth="0.9"
            />
            {points.filter((p) => p.conf != null).map((p, i) => (
              <circle key={`cd-${i}`} cx={xToPx(p.t)} cy={confToY(p.conf!)}
                      r="1.3" fill="#10b981" />
            ))}
          </>
        )}
      </svg>
    </ChartFrame>
  );
}

function fmtY(v: number, unit?: string): string {
  const n = Math.abs(v) >= 100 ? v.toFixed(0)
          : Math.abs(v) >= 10  ? v.toFixed(1)
          : v.toFixed(2);
  return unit ? `${n} ${unit}` : n;
}

/** 5-minute binning window. Recordings within the same 5-min
 *  wall-clock bucket get averaged into a single chart point. This
 *  gives finer resolution than the prior 30-min default — useful
 *  for spotting drift in classifier confidence over time within a
 *  single session. */
const BIN_MS = 5 * 60 * 1000;
const MS_PER_HOUR = 60 * 60 * 1000;

/** Bucket samples into 5-min wall-clock bins. Each bin returns the
 *  mean of its values AND the mean of the per-recording confidence
 *  scores (used for opacity-grading the plotted points + the
 *  confidence sub-trace). Confidence is the proxy for "how much
 *  should we trust this point?". Today this is the alignment
 *  confidence (per-recording) or per-cyl-health confidence (per-cyl
 *  charts); when the per-cyl SVM ships it'll be replaced with the
 *  classifier's per-class probability. */
function binInto5min(
  pts: Array<{ t: number; v: number; conf: number | null }>,
): Array<{ t: number; v: number; n: number; conf: number | null }> {
  if (pts.length === 0) return [];
  const buckets = new Map<number, {
    sum: number; n: number;
    confSum: number; confN: number;
  }>();
  for (const p of pts) {
    const key = Math.floor(p.t / BIN_MS);
    const e = buckets.get(key) ?? { sum: 0, n: 0, confSum: 0, confN: 0 };
    e.sum += p.v; e.n += 1;
    if (p.conf != null && Number.isFinite(p.conf)) {
      e.confSum += p.conf;
      e.confN += 1;
    }
    buckets.set(key, e);
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([key, e]) => ({
      t: key * BIN_MS + BIN_MS / 2,
      v: e.sum / e.n,
      n: e.n,
      conf: e.confN > 0 ? e.confSum / e.confN : null,
    }));
}

/** Distinct colors for cylinder annotations (matches CYL_COLORS in
 *  CylinderEnsembleView so cyl 3 looks the same wherever it's drawn). */
const CYL_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#a855f7', '#06b6d4', '#84cc16', '#ec4899',
  '#fb923c', '#14b8a6', '#a3e635', '#f472b6',
];

/** Map a confidence in [0, 1] to an SVG opacity in [0.25, 1.0]. Low-
 *  confidence points stay visible (so you see them dropping) but are
 *  clearly faded vs high-confidence neighbors. Falls back to 1.0
 *  when conf is null (don't penalize "no confidence info"). */
function confToOpacity(conf: number | null | undefined): number {
  if (conf == null || !Number.isFinite(conf)) return 1.0;
  return 0.25 + 0.75 * Math.max(0, Math.min(1, conf));
}

function fmtX(t: number): string {
  const d = new Date(t);
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const h = d.getHours();
  const min = d.getMinutes();
  return `${m}/${day} ${h}:${String(min).padStart(2, '0')}`;
}

interface WarningInfo {
  level: 'green' | 'yellow' | 'red';
  text: string;
}

function computeWarning({
  fit, lastVal, lastT, direction, warnAt, criticalAt, warnDeviation, criticalDeviation,
}: {
  fit: LinearFit | null; lastVal: number; lastT: number;
  direction: Direction;
  warnAt?: number; criticalAt?: number;
  warnDeviation?: number; criticalDeviation?: number;
}): WarningInfo | null {
  // Current state.
  let currentLevel: 'green' | 'yellow' | 'red' = 'green';
  let currentReason = '';
  if (direction === 'higher-better') {
    if (criticalAt != null && lastVal < criticalAt) {
      currentLevel = 'red'; currentReason = `currently below critical (${criticalAt})`;
    } else if (warnAt != null && lastVal < warnAt) {
      currentLevel = 'yellow'; currentReason = `currently below warning (${warnAt})`;
    }
  } else if (direction === 'lower-better') {
    if (criticalAt != null && lastVal > criticalAt) {
      currentLevel = 'red'; currentReason = `currently above critical (${criticalAt})`;
    } else if (warnAt != null && lastVal > warnAt) {
      currentLevel = 'yellow'; currentReason = `currently above warning (${warnAt})`;
    }
  } else if (direction === 'closer-to-100') {
    const dev = Math.abs(lastVal - 100);
    if (criticalDeviation != null && dev > criticalDeviation) {
      currentLevel = 'red'; currentReason = `Δ ${dev.toFixed(0)}% from cohort`;
    } else if (warnDeviation != null && dev > warnDeviation) {
      currentLevel = 'yellow'; currentReason = `Δ ${dev.toFixed(0)}% from cohort`;
    }
  }

  // Projection — only meaningful when slope is non-trivial and direction is right.
  let projectionText = '';
  if (fit && Math.abs(fit.slope) * MS_PER_DAY > 1e-6 && fit.r2 > 0.1) {
    const slopePerDay = fit.slope * MS_PER_DAY;
    let crossesAt: number | null = null;
    let label = '';
    if (direction === 'higher-better' && slopePerDay < 0 && currentLevel !== 'red') {
      const target = currentLevel === 'green' && warnAt != null ? warnAt
                    : criticalAt != null ? criticalAt : null;
      if (target != null && lastVal > target) {
        crossesAt = (target - lastVal) / slopePerDay;
        label = currentLevel === 'green' ? `warn (${target})` : `critical (${target})`;
      }
    } else if (direction === 'lower-better' && slopePerDay > 0 && currentLevel !== 'red') {
      const target = currentLevel === 'green' && warnAt != null ? warnAt
                    : criticalAt != null ? criticalAt : null;
      if (target != null && lastVal < target) {
        crossesAt = (target - lastVal) / slopePerDay;
        label = currentLevel === 'green' ? `warn (${target})` : `critical (${target})`;
      }
    }
    if (crossesAt != null && crossesAt > 0 && crossesAt < 365) {
      projectionText = ` — projected to cross ${label} in ~${Math.round(crossesAt)} days`;
    }
  }

  if (currentLevel === 'green' && projectionText === '') return null;
  return {
    level: currentLevel,
    text: (currentReason || 'trending') + projectionText,
  };
}

function ChartFrame({
  title, warning, children, statsFooter,
}: {
  title: string;
  warning?: WarningInfo | null;
  children: React.ReactNode;
  statsFooter?: React.ReactNode;
}) {
  const tone = warning?.level === 'red' ? 'var(--danger)'
             : warning?.level === 'yellow' ? '#f59e0b'
             : 'var(--border)';
  return (
    <div style={{
      background: 'var(--panel-2)',
      border: `1px solid ${tone}`,
      borderRadius: 6,
      padding: '0.4rem 0.6rem',
    }}>
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: '0.85rem' }}>{title}</strong>
        {warning && (
          <span style={{ fontSize: '0.7rem', color: tone }}>
            {warning.level === 'red' ? '⚠ ' : warning.level === 'yellow' ? '⚠ ' : ''}
            {warning.text}
          </span>
        )}
      </div>
      {children}
      {statsFooter}
    </div>
  );
}

/** Format the regression slope's significance into a tight footer
 *  line: slope/hour, R², p, verbal interpretation. Hour-scale slope
 *  matches the 30-min binning granularity of the chart points; daily
 *  slope is still used for the long-term failure projection. */
function formatSlopeStats(fit: LinearFit, unit?: string): React.ReactNode {
  const slopePerHour = fit.slope * MS_PER_HOUR;
  const fmtP = (p: number) =>
    p < 1e-6 ? '<1e-6'
      : p < 0.001 ? '<0.001'
      : p.toFixed(3);
  const stars = fit.slopeP < 0.001 ? '***'
              : fit.slopeP < 0.01 ? '**'
              : fit.slopeP < 0.05 ? '*'
              : '';
  const sig = fit.slopeP < 0.05;
  const verbal = !sig
    ? 'no significant trend'
    : fit.r2 < 0.1
      ? 'weak trend'
      : fit.r2 < 0.4
        ? 'moderate trend'
        : 'strong trend';
  // Slope display: avoid scientific-notation soup for tiny per-hour
  // changes; prefer 3 sig figs.
  const slopeStr = Math.abs(slopePerHour) >= 1
    ? slopePerHour.toFixed(2)
    : Math.abs(slopePerHour) >= 0.01
      ? slopePerHour.toFixed(3)
      : slopePerHour.toExponential(2);
  return (
    <div className="muted" style={{
      fontSize: '0.7rem', marginTop: 2, lineHeight: 1.35,
      color: sig ? 'var(--fg)' : 'var(--muted)',
    }}>
      slope {slopeStr}{unit ? `${unit}` : ''}/hour · R²={fit.r2.toFixed(2)} ·{' '}
      <span style={{ color: sig ? 'var(--accent)' : 'var(--muted)' }}>
        p={fmtP(fit.slopeP)}{stars}
      </span>{' '}
      · {verbal} (n={fit.n})
    </div>
  );
}

// ---- Per-cylinder trend (small-multiples) ----------------------------

interface PerCylinderTrendProps {
  samples: TrendSample[];
  repairEvents?: RepairEvent[];
  numCyl: number;
  /** Whether the per-slot extract resolves against the recording's own
   *  slot order or template-frame (persistent-cyl) order. Drives the
   *  per-chart label only — the caller still picks which array to
   *  read. */
  axis: CylAxis;
  title: string;
  extract: (s: TrendSample, slot: number) => number | null;
  direction: Direction;
  warnAt?: number;
  criticalAt?: number;
  warnDeviation?: number;
  criticalDeviation?: number;
}

function PerCylinderTrend({
  samples, repairEvents, numCyl, axis, title, extract, direction,
  warnAt, criticalAt, warnDeviation, criticalDeviation,
}: PerCylinderTrendProps) {
  return (
    <div style={{ marginBottom: '0.7rem' }}>
      <div style={{ fontSize: '0.85rem', marginBottom: 3 }}>{title}</div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(numCyl, 4)}, 1fr)`,
        gap: '0.4rem',
      }}>
        {Array.from({ length: numCyl }, (_, k) => (
          <TrendChart
            key={k}
            samples={samples} title={cylLabel(axis, k)} unit=""
            extract={(s) => extract(s, k)}
            // Per-cyl charts use the per-cyl confidence (slot-binning
            // confidence today; per-cyl SVM probability when that
            // ships) as the fade signal — drift in cyl-N's
            // identification reliability is what we want to surface
            // here, not the recording-wide alignment confidence.
            extractConf={(s) => {
              const arr = axis === 'cyl'
                ? s.cylinder_confidences_by_cyl
                : s.cylinder_confidences;
              return arr?.[k] ?? null;
            }}
            // Per-cyl repair events: include both events specific to
            // this cyl AND whole-engine events (cylinder_id=null).
            // We label cyls by physical number (1-based), so when the
            // axis is 'slot' we can match k+1 to cylinder_id directly.
            // For 'cyl' (template-frame) axis the mapping requires
            // the calibration assignments — we don't have them here,
            // so just show all events.
            repairEvents={repairEvents?.filter((ev) =>
              ev.cylinder_id == null
              || (axis === 'slot' && ev.cylinder_id === k + 1)
              || axis === 'cyl',
            )}
            direction={direction}
            warnAt={warnAt}
            criticalAt={criticalAt}
            warnDeviation={warnDeviation}
            criticalDeviation={criticalDeviation}
          />
        ))}
      </div>
    </div>
  );
}

// ---- Step I — Before-vs-after metric comparison per repair event ----
//
// For each logged repair event, computes the mean of selected metrics
// across the N samples immediately BEFORE the event vs the N samples
// AFTER. Surfaces the delta + a verdict (improved / no change /
// worsened). Closes the diagnostic loop: detect anomaly → user takes
// action → log repair → see effect.

const BEFORE_AFTER_N = 5;

interface MetricSpec {
  label: string;
  unit?: string;
  extract: (s: TrendSample, cylNum?: number | null) => number | null;
  /** Direction the user wants this metric to move post-repair. */
  direction: 'higher-better' | 'lower-better';
}

const WHOLE_ENGINE_METRICS: MetricSpec[] = [
  { label: 'Smoothness', extract: (s) => s.smoothness_score, direction: 'higher-better' },
  { label: 'Exhaust leak', extract: (s) => s.exhaust_leak_score, direction: 'lower-better' },
  { label: 'Vacuum leak', extract: (s) => s.vacuum_leak_score, direction: 'lower-better' },
];

const PER_CYL_METRICS: MetricSpec[] = [
  { label: 'Energy %', unit: '%',
    extract: (s, cyl) => cyl != null ? (s.cylinder_energies?.[cyl - 1] ?? null) : null,
    direction: 'higher-better' },
  { label: 'Variance %', unit: '%',
    extract: (s, cyl) => cyl != null ? (s.cylinder_variances?.[cyl - 1] ?? null) : null,
    direction: 'lower-better' },
  { label: 'Missed events', extract: (s, cyl) => cyl != null ? (s.cylinder_missed?.[cyl - 1] ?? null) : null, direction: 'lower-better' },
  { label: 'Knock severity', extract: (s, cyl) => cyl != null ? (s.cylinder_knock?.[cyl - 1] ?? null) : null, direction: 'lower-better' },
];

function meanOfNullable(xs: Array<number | null>): { mean: number | null; n: number } {
  const ys = xs.filter((v): v is number => v != null && Number.isFinite(v));
  if (ys.length === 0) return { mean: null, n: 0 };
  return { mean: ys.reduce((s, v) => s + v, 0) / ys.length, n: ys.length };
}

function RepairEffectsPanel({
  samples, repairEvents,
}: { samples: TrendSample[]; repairEvents: RepairEvent[] }) {
  const sortedSamples = useMemo(
    () => samples.slice().sort((a, b) => a.recorded_at - b.recorded_at),
    [samples],
  );
  const sortedEvents = useMemo(
    () => repairEvents.slice().sort((a, b) => b.occurred_at - a.occurred_at),
    [repairEvents],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div className="muted" style={{ fontSize: '0.78rem' }}>
        For each logged repair, mean of {BEFORE_AFTER_N} samples before
        vs {BEFORE_AFTER_N} samples after. Delta = (after − before).
        Verdict: ✓ improvement = metric moved in the desired direction
        by ≥ 5 % of before; × worsened = moved in the WRONG direction
        by ≥ 5 %; — = no change of note.
      </div>
      {sortedEvents.map((ev) => (
        <RepairEffectCard key={ev.id} samples={sortedSamples} event={ev} />
      ))}
    </div>
  );
}

function RepairEffectCard({
  samples, event,
}: { samples: TrendSample[]; event: RepairEvent }) {
  // Find the N samples immediately before and after the event.
  const beforeSamples: TrendSample[] = [];
  const afterSamples: TrendSample[] = [];
  for (const s of samples) {
    if (s.recorded_at < event.occurred_at) beforeSamples.push(s);
    else if (s.recorded_at > event.occurred_at) afterSamples.push(s);
  }
  const before = beforeSamples.slice(-BEFORE_AFTER_N);
  const after  = afterSamples.slice(0, BEFORE_AFTER_N);

  const cylLabel = event.cylinder_id != null
    ? `Cyl ${event.cylinder_id}`
    : 'Engine-wide';
  const kindLabel = REPAIR_KIND_LABELS[event.repair_kind] ?? event.repair_kind;
  const dateLabel = new Date(event.occurred_at).toLocaleString();
  const color = event.cylinder_id != null
    ? CYL_COLORS[(event.cylinder_id - 1) % CYL_COLORS.length]
    : 'var(--muted)';

  const metrics = event.cylinder_id != null
    ? [...WHOLE_ENGINE_METRICS, ...PER_CYL_METRICS]
    : WHOLE_ENGINE_METRICS;

  return (
    <div style={{
      background: 'var(--panel-2)',
      border: `1px solid ${color}`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 6,
      padding: '0.5rem 0.7rem',
    }}>
      <div className="row" style={{
        alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4,
      }}>
        <div>
          <strong>{cylLabel} · {kindLabel}</strong>
          {event.notes && (
            <span className="muted" style={{ marginLeft: 8, fontSize: '0.85rem' }}>
              ({event.notes})
            </span>
          )}
        </div>
        <span className="muted" style={{ fontSize: '0.78rem' }}>{dateLabel}</span>
      </div>
      {before.length === 0 && after.length === 0 ? (
        <div className="muted" style={{ fontSize: '0.78rem' }}>
          No samples before or after this event yet.
        </div>
      ) : (
        <table style={{ fontSize: '0.85rem', width: '100%' }}>
          <thead>
            <tr style={{ color: 'var(--muted)' }}>
              <th style={{ textAlign: 'left' }}>metric</th>
              <th style={{ textAlign: 'right' }}>before (n={before.length})</th>
              <th style={{ textAlign: 'right' }}>after (n={after.length})</th>
              <th style={{ textAlign: 'right' }}>Δ</th>
              <th style={{ textAlign: 'center' }}>verdict</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => {
              const beforeM = meanOfNullable(before.map((s) => m.extract(s, event.cylinder_id)));
              const afterM  = meanOfNullable(after.map((s) => m.extract(s, event.cylinder_id)));
              if (beforeM.mean == null || afterM.mean == null) {
                return (
                  <tr key={m.label}>
                    <td>{m.label}</td>
                    <td className="muted" colSpan={4} style={{ textAlign: 'center' }}>
                      insufficient data
                    </td>
                  </tr>
                );
              }
              const delta = afterM.mean - beforeM.mean;
              const pctOfBefore = beforeM.mean !== 0
                ? Math.abs(delta / beforeM.mean) * 100
                : 0;
              let verdict: 'improved' | 'worsened' | 'flat' = 'flat';
              if (pctOfBefore >= 5) {
                if (m.direction === 'higher-better') {
                  verdict = delta > 0 ? 'improved' : 'worsened';
                } else {
                  verdict = delta < 0 ? 'improved' : 'worsened';
                }
              }
              const verdictColor = verdict === 'improved' ? 'var(--ok)'
                : verdict === 'worsened' ? 'var(--danger)'
                : 'var(--muted)';
              const verdictMark = verdict === 'improved' ? '✓ improved'
                : verdict === 'worsened' ? '× worsened'
                : '— no change';
              return (
                <tr key={m.label}>
                  <td>{m.label}{m.unit ? ` (${m.unit})` : ''}</td>
                  <td style={{ textAlign: 'right' }}>{beforeM.mean.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{afterM.mean.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {delta >= 0 ? '+' : ''}{delta.toFixed(2)}
                    {' '}
                    <span className="muted" style={{ fontSize: '0.78rem' }}>
                      ({delta >= 0 ? '+' : ''}{(delta / Math.max(Math.abs(beforeM.mean), 1e-12) * 100).toFixed(0)}%)
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', color: verdictColor, fontWeight: 600 }}>
                    {verdictMark}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
